import { createApp } from '~/vue/createApp';
import NotificationsStack from './NotificationsStack.vue';
import normalizeErrorMessage from './normalizeErrorMessage';
import './notifications.css';

const DEFAULT_DURATION = 2500;
const DEFAULT_CONTAINER = 'body';

const stacks = new Map();
let lastId = 0;

/**
 * One stack (a fixed positioned column of toasts) per container selector.
 * Containers other than `body` are used to keep notifications visible while some
 * element is in fullscreen mode - see the goals timeline.
 */
function getStack(containerSelector = DEFAULT_CONTAINER) {
    const existingStack = stacks.get(containerSelector);
    if (existingStack && existingStack.$el.isConnected) {
        return existingStack;
    }
    const containerEl = document.querySelector(containerSelector) || document.body;
    const mountPoint = document.createElement('div');
    containerEl.appendChild(mountPoint);
    // the stack is the root of its own app, so mount() hands it back directly - it used to
    // be rendered as the child of a throwaway root and picked out of `$children`, which
    // vue 3 removed
    const stack = createApp(NotificationsStack).mount(mountPoint);
    stacks.set(containerSelector, stack);
    return stack;
}

/**
 * @param {object|string} args message, or {message, title, delay, container, component, props,
 *                             closeOnClick, onClose, type}. `component` renders a Vue component
 *                             inside the toast instead of the `message` html.
 * @param {string} [type] one of '', 'success', 'error', 'warning', 'info'
 */
function notify(args, type) {
    if (typeof args !== 'object' || args === null) {
        args = {message: args};
    }
    const notification = {
        id: ++lastId,
        type: type || args.type || '',
        title: args.title,
        message: args.message,
        component: args.component,
        props: args.props || {},
        delay: args.delay === undefined ? DEFAULT_DURATION : args.delay,
        closeOnClick: args.closeOnClick !== false,
        onClose: args.onClose
    };
    const stack = getStack(args.container);
    stack.add(notification);
    return {
        id: notification.id,
        close: () => stack.remove(notification.id)
    };
}

function clearAll() {
    for (const stack of stacks.values()) {
        stack.clear();
    }
}

// kept for the few call sites that need a fully custom toast
const Notification = (args, type) => notify(args, type);
Notification.primary = args => notify(args, 'primary');
Notification.error = args => notify(args, 'error');
Notification.success = args => notify(args, 'success');
Notification.info = args => notify(args, 'info');
Notification.warning = args => notify(args, 'warning');
Notification.clearAll = clearAll;

const notifications = {
    success(message, duration = DEFAULT_DURATION, container = DEFAULT_CONTAINER) {
        return notify({message, delay: duration, container}, 'success');
    },
    error(message = 'Error', duration = DEFAULT_DURATION, container = DEFAULT_CONTAINER) {
        return notify({message: normalizeErrorMessage(message), delay: duration, container}, 'error');
    },
    warning(message, duration = DEFAULT_DURATION, container = DEFAULT_CONTAINER) {
        return notify({message, delay: duration, container}, 'warning');
    },
    message(message, duration = DEFAULT_DURATION, container = DEFAULT_CONTAINER) {
        return notify({message, delay: duration, container}, 'primary');
    },
    notify,
    clearAll,
    Notification
};

// `this.$notifications` for the components and the templates, which have nothing to import
// from. Everything else imports the default export: the object above is the singleton, and
// `Vue.notifications` off the compat global - which this plugin used to install itself onto
// as the module loaded - was only ever another name for it
const VueNotifications = {
    install(app) {
        app.config.globalProperties.$notifications = notifications;
    }
};

export default notifications;
export {VueNotifications, notify, normalizeErrorMessage};
